const TIME_DIMENSIONS = ["day", "week", "month"];
const DIMENSION_LABELS = {
    day: "Day",
    week: "Week",
    month: "Month",
    campaign: "Campaign Name",
    publisher: "Publisher Name",
    age: "Age",
    send_time: "Send Time",
    weekday: "Week Day",
};
const BREAKDOWN_LABELS = {
    campaign_name: "Campaign Name",
    adset_name: "Ad Set Name",
    ad_name: "Ad Name",
};
/** "adset_name" → "Adset Name", for a key the maps above do not know. */
const titleCase = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
const dimensionLabel = (key) => DIMENSION_LABELS[key] || titleCase(key);
const breakdownLabel = (key) => BREAKDOWN_LABELS[key] || titleCase(key);
/** API network key → builder key, where the two differ. */
const toBuilderNetwork = (apiKey) => (apiKey === "ga" ? "google_analytics" : apiKey);
/** Custom metric formats are the API's own words. */
const CUSTOM_FORMATS = {
    currency: "currency",
    percentage: "percent",
    numeric: "number",
};
/**
 * The catalog carries no format, so it is read off the metric key — money as
 * currency, rates as percent, ROAS as a ratio.
 */
const formatForMetric = (metric) => {
    const key = metric.toLowerCase();
    if (key.includes("roas"))
        return "ratio";
    if (/(ctr|rate|share|percent)/.test(key))
        return "percent";
    if (/(spend|cost|revenue|value|sales|cpc|cpm|cpa|aov|price|discount|return|amount)/.test(key)) {
        return "currency";
    }
    return "number";
};
const toNumbers = (values) => {
    if (!values)
        return null;
    const out = {};
    Object.entries(values).forEach(([key, value]) => {
        const num = Number(value);
        if (Number.isFinite(num))
            out[key] = num;
    });
    return out;
};
/** Networks that put a value on this row, from the "<network>." prefixes. */
const networksOf = (metrics) => {
    const keys = [];
    Object.keys(metrics || {}).forEach((key) => {
        const dot = key.indexOf(".");
        if (dot < 0)
            return;
        const network = toBuilderNetwork(key.slice(0, dot));
        if (!keys.includes(network))
            keys.push(network);
    });
    return keys;
};
export const toGeneratedReport = (data, state, query) => {
    // One table per network: each is mapped exactly like a whole report. The
    // outer report carries only the period totals and the list of tables.
    if (data.tables?.length) {
        return {
            query,
            series: [],
            columns: [],
            rows: [],
            totals: toNumbers(data.primary) || {},
            compare_totals: toNumbers(data.comparison),
            generated_at: data.last_updated_at ?? null,
            row_count: 0,
            tables: data.tables.map((table) => {
                // A network with none of the picked dimensions and no time cut
                // (Pepperjam under Campaign alone) comes back with no rows, only
                // its totals - an empty table. Show its period total as the one
                // row it has.
                const uncut = !table.dimensions?.length && !table.rows?.length && !table.subtotals?.length;
                const source = uncut
                    ? {
                        ...table,
                        rows: [
                            {
                                dimension: null,
                                dimensions: null,
                                metrics: table.primary,
                                comparison: table.comparison,
                            },
                        ],
                    }
                    : table;
                return {
                    network: table.network,
                    label: table.label,
                    skipped: (table.skipped_dimensions || []).map(dimensionLabel),
                    // Each table is laid out by the dimensions IT was cut by — the
                    // table's own `dimensions`, read below like any report's.
                    // The heading already names what it skipped.
                    report: { ...toGeneratedReport({ ...source, tables: null }, state, query), skipped: [] },
                };
            }),
        };
    }
    // The dimensions the API actually cut by; the selection only as a fallback.
    const dims = data.dimensions ?? state.dimension_ids;
    const skipped = (data.skipped_dimensions || []).map(dimensionLabel);
    const timeKey = dims.find((key) => TIME_DIMENSIONS.includes(key)) ?? null;
    const fieldKeys = dims.filter((key) => !TIME_DIMENSIONS.includes(key));
    const hasCompare = Boolean(data.comparison);
    // Ticked breakdown fields across networks, in first-seen order (Meta's
    // coarsest → finest).
    const breakdownKeys = [];
    // Campaign Name as a breakdown repeats the Campaign dimension's own column,
    // so it never gets a column of its own.
    (data.rows || []).forEach((row) => Object.keys(row.breakdown || {}).forEach((key) => {
        if (key === "campaign_name" && dims.includes("campaign"))
            return;
        if (!breakdownKeys.includes(key))
            breakdownKeys.push(key);
    }));
    const metricColumns = data.columns.map((column) => {
        const dot = column.key.indexOf(".");
        return {
            id: column.key,
            label: column.label,
            format: column.is_custom
                ? CUSTOM_FORMATS[column.format || ""] || "number"
                : formatForMetric(dot > 0 ? column.key.slice(dot + 1) : column.key),
            network: dot > 0 ? toBuilderNetwork(column.key.slice(0, dot)) : null,
            is_dimension: false,
            is_custom: Boolean(column.is_custom),
            description: column.description || "",
        };
    });
    /** Label of one dimension on a row, "-" where the row's network has none. */
    const labelOf = (row, key) => row.dimensions?.[key]?.label ?? (row.dimension && key === dims[0] ? row.dimension.label : "-");
    const toRow = (row, id, labels, extraProps) => ({
        id,
        dimensions: labels,
        compare_dimensions: hasCompare && row.compare_dimension ? { [timeKey || dims[0]]: row.compare_dimension.label } : null,
        values: toNumbers(row.metrics) || {},
        compare_values: hasCompare ? toNumbers(row.comparison) : null,
        compare_only: row.compare_only || undefined,
        network_keys: networksOf(row.metrics),
        ad_id: row.ad_id || undefined,
        account_id: row.account_id || undefined,
        connection_id: row.connection_id || undefined,
        status: extraProps?.status || row.dimensions?.campaign?.status || row.dimension?.status || undefined,
    });
    const base = {
        query,
        series: [],
        totals: toNumbers(data.primary) || {},
        compare_totals: toNumbers(data.comparison),
        generated_at: data.last_updated_at ?? null,
        skipped,
    };
    // ── Tree: Day › Campaign › Ad set › Ad ───────────────────────────────
    // The API sends the leaf rows plus `subtotals`: one total per node above
    // them. Every node is identified by its path (its value at each level), so
    // rows hang under the node whose path is theirs minus the last level. A
    // network cut into fewer levels (Google beside a Meta ad breakdown) has leaf
    // rows at a shallower depth; a node Meta and Google share is merged.
    if (data.subtotals?.length) {
        // The same levels the API cut the rows into, top first.
        const mainKey = timeKey ?? fieldKeys[0];
        const levels = [
            mainKey,
            ...fieldKeys.filter((key) => key !== mainKey),
            ...breakdownKeys.filter((key) => !(key === "campaign_name" && fieldKeys.includes("campaign"))),
        ];
        const isDimension = (level) => dims.includes(level);
        const SEP = "\u0000";
        /** A row's value at each level, down to the first level it has none for. */
        const rowPath = (row) => {
            const keys = [];
            const labels = [];
            for (const level of levels) {
                const value = isDimension(level)
                    ? row.dimensions?.[level] ?? (level === mainKey ? row.dimension : null)
                    : row.breakdown?.[level] != null
                        ? { key: row.breakdown[level], label: row.breakdown[level] }
                        : null;
                if (!value)
                    break;
                keys.push(value.key);
                labels.push(value.label);
            }
            return { keys, labels };
        };
        const nodes = new Map();
        const order = [];
        const addNode = (keys, label, source, compareLabel) => {
            const id = keys.join(SEP);
            const existing = nodes.get(id);
            const values = toNumbers(source.metrics) || {};
            const compareValues = hasCompare ? toNumbers(source.comparison) : null;
            const apiRow = source;
            if (existing) {
                // The same node from another network — merge its columns in.
                Object.assign(existing.values, values);
                if (existing.compare_values && compareValues)
                    Object.assign(existing.compare_values, compareValues);
                if (!existing.ad_id && apiRow?.ad_id)
                    existing.ad_id = apiRow.ad_id;
                if (!existing.account_id && apiRow?.account_id)
                    existing.account_id = apiRow.account_id;
                if (!existing.connection_id && apiRow?.connection_id)
                    existing.connection_id = apiRow.connection_id;
                return;
            }
            nodes.set(id, {
                id: `n-${order.length}`,
                depth: keys.length,
                dimensions: { __tree: label || "-" },
                compare_dimensions: keys.length === 1 && compareLabel ? { __tree: compareLabel } : null,
                values,
                compare_values: compareValues,
                compare_only: source.compare_only || undefined,
                children: [],
                ad_id: apiRow?.ad_id || undefined,
                account_id: apiRow?.account_id || undefined,
                connection_id: apiRow?.connection_id || undefined,
            });
            order.push(id);
        };
        data.subtotals.forEach((node) => {
            const keys = levels.slice(0, node.depth).map((level) => node.path[level] ?? "");
            const label = node.labels[levels[node.depth - 1]] ?? "";
            addNode(keys, label, node, node.compare_label);
        });
        (data.rows || []).forEach((row) => {
            const { keys, labels } = rowPath(row);
            if (keys.length === 0)
                return;
            addNode(keys, labels[labels.length - 1], row, row.compare_dimension?.label ?? null);
        });
        // Hang every node under its parent — the path one level shorter.
        const roots = [];
        order.forEach((id) => {
            const node = nodes.get(id);
            const parentId = id.split(SEP).slice(0, -1).join(SEP);
            const parent = (node.depth || 1) > 1 ? nodes.get(parentId) : undefined;
            if (parent)
                parent.children.push(node);
            else
                roots.push(node);
        });
        // Days read chronologically: their key is ymd / YYYY-MM.
        if (timeKey) {
            const keyOf = new Map(order.map((id) => [nodes.get(id).id, id]));
            // Plain compare, not localeCompare: a compare-only day's key leads
            // with "~", which localeCompare would rank before every digit.
            roots.sort((a, b) => {
                const left = keyOf.get(a.id) || "";
                const right = keyOf.get(b.id) || "";
                return left < right ? -1 : left > right ? 1 : 0;
            });
        }
        // One column per level, as Meta's pivot lays it out: a node fills its
        // own level's cell, leaves its ancestors' cells blank (they are the
        // rows above it) and reads "All" in the levels below it. A leaf that
        // stops short — a Google campaign beside Meta's ads — reads "-" there.
        const fillCells = (list) => list.forEach((node) => {
            const depth = node.depth || 1;
            const isLeaf = !node.children?.length;
            const own = node.dimensions.__tree;
            const cells = {};
            levels.forEach((level, index) => {
                cells[`lvl_${level}`] =
                    index < depth - 1 ? "" : index === depth - 1 ? own : isLeaf ? "-" : "All";
            });
            node.dimensions = cells;
            if (node.compare_dimensions?.__tree) {
                node.compare_dimensions = { [`lvl_${levels[0]}`]: node.compare_dimensions.__tree };
            }
            fillCells(node.children || []);
        });
        fillCells(roots);
        const levelLabel = (level) => isDimension(level)
            ? level === "campaign" ? "Campaign name" : dimensionLabel(level)
            : breakdownLabel(level);
        return {
            ...base,
            grouped: true,
            columns: [
                ...levels.map((level) => ({
                    id: `__dimension__lvl_${level}`,
                    label: levelLabel(level),
                    format: null,
                    network: null,
                    is_dimension: true,
                })),
                ...metricColumns,
            ],
            rows: roots,
            row_count: roots.length,
        };
    }
    // Check if any row has extra campaign dimensions (status, send_time, weekday)
    const hasExtraCampaignDims = dims.includes("campaign") && (data.rows || []).some((row) => row.dimension?.send_time != null ||
        row.dimension?.weekday != null ||
        row.dimension?.status != null ||
        row.dimensions?.campaign?.send_time != null ||
        row.dimensions?.campaign?.weekday != null ||
        row.dimensions?.campaign?.status != null);
    // ── Flat: one label column per dimension and per breakdown ───────────
    const rows = (data.rows || []).map((row, index) => {
        const campaignDim = row.dimensions?.campaign || (dims[0] === "campaign" ? row.dimension : null);
        const status = campaignDim?.status;
        const sendTime = campaignDim?.send_time || "-";
        const weekday = campaignDim?.weekday || "-";
        const labels = {};
        dims.forEach((key) => {
            labels[key] = labelOf(row, key);
            if (key === "campaign" && hasExtraCampaignDims) {
                labels.send_time = sendTime;
                labels.weekday = weekday;
                if (status)
                    labels.status = status;
            }
        });
        breakdownKeys.forEach((key) => {
            labels[`bd_${key}`] = row.breakdown?.[key] ?? "-";
        });
        return toRow(row, `r-${index}`, labels, { status });
    });
    const dimensionCols = [];
    dims.forEach((key) => {
        dimensionCols.push({
            id: `__dimension__${key}`,
            label: dimensionLabel(key),
            format: null,
            network: null,
            is_dimension: true,
        });
        if (key === "campaign" && hasExtraCampaignDims) {
            dimensionCols.push({
                id: `__dimension__send_time`,
                label: "Send Time",
                format: null,
                network: null,
                is_dimension: true,
            }, {
                id: `__dimension__weekday`,
                label: "Week Day",
                format: null,
                network: null,
                is_dimension: true,
            });
        }
    });
    return {
        ...base,
        columns: [
            ...dimensionCols,
            ...breakdownKeys.map((key) => ({
                id: `__dimension__bd_${key}`,
                label: breakdownLabel(key),
                format: null,
                network: null,
                is_dimension: true,
            })),
            ...metricColumns,
        ],
        rows,
        row_count: rows.length,
    };
};
